/**
 * Transport: REST plus the realtime socket.
 *
 * The server address is configurable so one build can point at the production
 * VPS, a LAN box, or localhost - the desktop app, the web build and a dev
 * server all run the same code.
 */

import { bridge } from './bridge';
import type {
  Session as DeviceSession, Activity,
  Catalog, Friendship, Group, Message, Party, PublicUser, Settings
} from './types';

const LS_SERVER = 'pogu.server';
const LS_TOKEN = 'pogu.token';

declare global {
  interface Window {
    POGU_DEFAULT_SERVER?: string;
  }
}

export const DEFAULT_SERVER =
  (typeof window !== 'undefined' && window.POGU_DEFAULT_SERVER) || 'https://pugo.ink';

export function serverUrl(): string {
  const saved = localStorage.getItem(LS_SERVER);
  /*
   * A saved plain-HTTP address is no use on an HTTPS page.
   *
   * Everyone who used the app before it had a domain has the old
   * http://87.106.70.39:8791 sitting in storage. Loading https://pugo.ink
   * with that still set means every request is mixed content and the browser
   * blocks all of them - a blank app with no visible reason. The stale value
   * is dropped rather than obeyed.
   */
  if (saved && /^http:/i.test(saved) && location.protocol === 'https:') {
    localStorage.removeItem(LS_SERVER);
    return DEFAULT_SERVER;
  }
  return saved || DEFAULT_SERVER;
}

/** Absolute for anything the server hands back as a bare path. */
export function fileUrl(url: string): string {
  return /^https?:/i.test(url) ? url : serverUrl() + url;
}

export function setServerUrl(url: string | null): void {
  const clean = String(url ?? '').trim().replace(/\/+$/, '');
  if (clean) localStorage.setItem(LS_SERVER, clean);
  else localStorage.removeItem(LS_SERVER);
}

export function token(): string | null {
  return localStorage.getItem(LS_TOKEN);
}

export function setToken(value: string | null): void {
  if (value) localStorage.setItem(LS_TOKEN, value);
  else localStorage.removeItem(LS_TOKEN);

  /*
   * Mirrored outside the renderer, on the desktop.
   *
   * localStorage belongs to the origin, and the origin includes the port the
   * renderer is served on. Anything that moves that port takes the session
   * with it. The desktop keeps its own encrypted copy so a sign-in survives
   * whatever the loopback server ends up listening on.
   */
  void bridge()?.saveSession?.(value);
}

/**
 * The token, from wherever it survived.
 *
 * Falls back to the desktop's own copy when the renderer's storage has come
 * up empty - which is exactly what happened on every launch while the port
 * was random.
 */
export async function restoreToken(): Promise<string | null> {
  const here = token();
  if (here) return here;

  const kept = await bridge()?.loadSession?.().catch(() => null);
  if (kept) {
    localStorage.setItem(LS_TOKEN, kept);
    return kept;
  }
  return null;
}

export class ApiError extends Error {
  status: number;
  code: string | null;
  /**
   * Whatever else came back with the refusal.
   *
   * Some failures carry something the caller needs - a mistyped two-factor
   * code comes back with a fresh sign-in ticket, so a typo does not send you
   * to the start. Spread onto the error, so it reads as err.ticket.
   */
  constructor(message: string, status: number, code: string | null = null, extra?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    if (extra) Object.assign(this, extra);
  }
}

type Method = 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';

async function request<T>(method: Method, path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const t = token();
  if (t) headers.Authorization = 'Bearer ' + t;

  let res: Response;
  try {
    res = await fetch(serverUrl() + path, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal
    });
  } catch {
    throw new ApiError(
      'Cannot reach the server. Check your connection or the address in Settings.',
      0,
      'offline'
    );
  }

  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try { data = JSON.parse(text); } catch { data = null; }
  }

  if (!res.ok) {
    const payload = data as { error?: string; code?: string } | null;
    // Everything but the message and the code rides along on the error.
    const { error: _msg, code: _code, ...extra } = (payload ?? {}) as Record<string, unknown>;
    throw new ApiError(
      payload?.error ?? `Request failed (${res.status})`,
      res.status,
      payload?.code ?? null,
      extra
    );
  }
  return data as T;
}

export const api = {
  get: <T,>(path: string, signal?: AbortSignal) => request<T>('GET', path, undefined, signal),
  post: <T,>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  patch: <T,>(path: string, body?: unknown) => request<T>('PATCH', path, body ?? {}),
  put: <T,>(path: string, body?: unknown) => request<T>('PUT', path, body ?? {}),
  del: <T,>(path: string, body?: unknown) => request<T>('DELETE', path, body),

  qs(params: Record<string, string | number | boolean | undefined | null>): string {
    const parts = Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
    return parts.length ? '?' + parts.join('&') : '';
  }
};

/* ------------------------------------------------------ endpoints */

export type Session = { user: PublicUser; settings: Settings; token?: string };

/** What a sign-in comes back with: a session, or a demand for a second factor. */
export type LoginResult = {
  token?: string;
  user?: PublicUser;
  needsVerify?: boolean;
  /** Set when the password was right but the account has two-factor on. */
  twoFactor?: boolean;
  method?: 'app' | 'email';
  ticket?: string;
  hint?: string | null;
};

export const endpoints = {
  login: (username: string, password: string) =>
    api.post<LoginResult>('/api/auth/login', { username, password }),
  /** The second half, with the code from the app, the inbox or a recovery slip. */
  twoFactor: (ticket: string, code: string) =>
    api.post<LoginResult>('/api/auth/2fa', { ticket, code }),
  register: (username: string, password: string, email: string, displayName?: string) =>
    api.post<LoginResult & { bootstrapAdmin: boolean }>('/api/auth/register', {
      username, password, email, displayName
    }),
  verifyEmail: (code: string) => api.post<{ ok: boolean; user: PublicUser }>('/api/auth/verify', { code }),
  resendVerify: () => api.post<{ ok: boolean; sent: string }>('/api/auth/verify/resend', {}),
  changeEmail: (email: string, password: string) =>
    api.post<{ ok: boolean; email: string; sent: string }>('/api/auth/email', { email, password }),
  forgot: (email: string) => api.post<{ ok: boolean }>('/api/auth/forgot', { email }),
  resetPassword: (email: string, code: string, password: string) =>
    api.post<{ token: string; user: PublicUser }>('/api/auth/reset', { email, code, password }),
  me: () => api.get<{ user: PublicUser; settings: Settings }>('/api/auth/me'),
  catalog: () => api.get<Catalog>('/api/catalog'),

  parties: (query: Record<string, string | undefined>) =>
    api.get<{ parties: Party[]; total: number }>('/api/parties' + api.qs(query)),
  party: (id: string) => api.get<{ party: Party; messages: Message[] }>('/api/parties/' + id),
  joinParty: (id: string) => api.post<{ party: Party }>(`/api/parties/${id}/join`),
  leaveParty: (id: string) => api.post<{ ok: true; closed: boolean }>(`/api/parties/${id}/leave`),
  boostParty: (id: string, boosted: boolean) =>
    api.post<{ party: Party }>(`/api/admin/parties/${id}/boost`, { boosted }),

  friends: () => api.get<{
    friends: Friendship[]; incoming: Friendship[]; outgoing: Friendship[]; blocked: Friendship[];
  }>('/api/friends'),

  user: (username: string) => api.get<{
    user: PublicUser;
    relation?: { status: string; actorId: string; id: string } | null;
    mutuals?: PublicUser[];
    parties?: Party[];
    admin?: Record<string, unknown>;
  }>('/api/users/' + encodeURIComponent(username)),

  /** Where you are signed in, and how to sign a device out. */
  sessions: () => api.get<{ sessions: DeviceSession[] }>('/api/me/sessions'),
  revokeSession: (id: string) => api.del<{ ok: true }>('/api/me/sessions/' + id),
  revokeOtherSessions: () => api.post<{ ok: true; revoked: number }>('/api/me/sessions/revoke-others'),

  /** What somebody has been doing, and what their friends have. */
  userActivity: (username: string) =>
    api.get<{ activity: Activity[] }>('/api/users/' + encodeURIComponent(username) + '/activity'),
  friendActivity: () => api.get<{ activity: Activity[] }>('/api/friends/activity'),

  /** Parties somebody has been in - their match history. */
  userParties: (username: string) =>
    api.get<{ parties: (Party & { role: string; joinedAt: number })[] }>(
      '/api/users/' + encodeURIComponent(username) + '/parties'),

  /** Pinning one message, and read markers. */
  /* ---- group conversations ---- */
  groups: () => api.get<{ groups: Group[] }>('/api/groups'),
  group: (id: string) => api.get<{ group: Group }>('/api/groups/' + id),
  groupLog: (id: string) => api.get<{ channel: string; group: Group; messages: Message[] }>('/api/messages/group/' + id),
  createGroup: (name: string, members: string[]) =>
    api.post<{ group: Group; refused: string[] }>('/api/groups', { name, members }),
  renameGroup: (id: string, name: string) =>
    api.patch<{ group: Group }>('/api/groups/' + id, { name }),
  setGroupInvites: (id: string, openInvites: boolean) =>
    api.patch<{ group: Group }>('/api/groups/' + id, { openInvites }),
  addToGroup: (id: string, username: string) =>
    api.post<{ group: Group }>('/api/groups/' + id + '/members', { username }),
  removeFromGroup: (id: string, userId: string) =>
    api.del<{ ok: true }>('/api/groups/' + id + '/members/' + userId),
  setGroupRole: (id: string, userId: string, role: 'admin' | 'member') =>
    api.post<{ group: Group }>('/api/groups/' + id + '/members/' + userId + '/role', { role }),
  handOverGroup: (id: string, userId: string) =>
    api.post<{ group: Group }>('/api/groups/' + id + '/owner', { userId }),

  pinMessage: (id: string, pinned: boolean) => api.post<{ ok: true }>('/api/messages/' + id + '/pin', { pinned }),
  markRead: (channelId: string, at: number) => api.post<{ ok: true }>('/api/messages/read', { channelId, at }),
  reads: (channelId: string) =>
    api.get<{ reads: Record<string, number> }>('/api/messages/reads?channelId=' + encodeURIComponent(channelId)),

  updateMe: (patch: Partial<Record<string, unknown>>) =>
    api.patch<{ user: PublicUser }>('/api/me', patch),
  updateSettings: (patch: Partial<Settings>) =>
    api.patch<{ settings: Settings }>('/api/me/settings', patch)
};

/* ------------------------------------------------------ socket */

export type SocketState = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed';
type Handler = (data: any) => void;

class Socket {
  private ws: WebSocket | null = null;
  private listeners = new Map<string, Set<Handler>>();
  private attempt = 0;
  private closedByUs = false;
  private queue: string[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;

  state: SocketState = 'idle';

  on(type: string, fn: Handler): () => void {
    if (!this.listeners.has(type)) this.listeners.set(type, new Set());
    this.listeners.get(type)!.add(fn);
    return () => this.off(type, fn);
  }

  off(type: string, fn: Handler): void {
    this.listeners.get(type)?.delete(fn);
  }

  private emit(type: string, data: unknown): void {
    for (const fn of this.listeners.get(type) ?? []) {
      try { fn(data); } catch (err) { console.error('[socket]', type, err); }
    }
    for (const fn of this.listeners.get('*') ?? []) {
      try { (fn as unknown as (t: string, d: unknown) => void)(type, data); } catch { /* ignore */ }
    }
  }

  private setState(next: SocketState): void {
    if (this.state === next) return;
    this.state = next;
    this.emit('state', next);
  }

  connect(): void {
    const t = token();
    if (!t) return;
    this.closedByUs = false;
    const base = serverUrl().replace(/^http/, 'ws');
    this.setState(this.attempt ? 'reconnecting' : 'connecting');

    let ws: WebSocket;
    try {
      ws = new WebSocket(`${base}/ws?token=${encodeURIComponent(t)}`);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      this.attempt = 0;
      this.setState('open');
      for (const msg of this.queue.splice(0)) ws.send(msg);
    };
    ws.onmessage = (ev) => {
      let msg: { type: string; data: unknown };
      try { msg = JSON.parse(ev.data as string); } catch { return; }
      this.emit(msg.type, msg.data);
    };
    ws.onclose = (ev) => {
      this.ws = null;
      this.setState('closed');
      if (ev.code === 4001 || this.closedByUs) return;
      this.scheduleReconnect();
    };
    ws.onerror = () => { /* onclose follows */ };
  }

  private scheduleReconnect(): void {
    this.attempt++;
    const delay = Math.min(1000 * Math.pow(1.6, Math.min(this.attempt, 8)), 20000);
    this.setState('reconnecting');
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => this.connect(), delay);
  }

  send(type: string, data?: unknown): void {
    const msg = JSON.stringify({ type, data: data ?? {} });
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(msg);
    else if (this.queue.length < 50) this.queue.push(msg);
  }

  close(): void {
    this.closedByUs = true;
    if (this.timer) clearTimeout(this.timer);
    try { this.ws?.close(); } catch { /* already gone */ }
    this.ws = null;
    this.setState('idle');
  }
}

export const socket = new Socket();
